import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _extends from "@babel/runtime/helpers/extends";
var _excluded = ["targetRef", "placement", "matchWidth", "showArrow", "shouldCloseOnBlur", "onClose", "children", "classNameSuffix"];
import React, { forwardRef, useEffect, useRef } from 'react';
import EzPortal, { PortalContext } from '../EzPortal';
import theme from '../theme.config';
import { usePopper } from '../../utils/hooks';
import { useCloseOnBlur } from './useCloseOnBlur';
import FocusScope from '../utils/FocusScope';
import { clsx, mergeRefs } from '../../utils';
var popperStyle = theme.css({
  zIndex: '$popover-z'
});
var EzPopover = forwardRef(function (props, ref) {
  return React.createElement(EzPortal, null, React.createElement(EzPopoverImpl, _extends({
    ref: ref
  }, props)));
});
var EzPopoverImpl = forwardRef(function (_ref, ref) {
  var targetRef = _ref.targetRef,
    _ref$placement = _ref.placement,
    placement = _ref$placement === void 0 ? 'bottom' : _ref$placement,
    _ref$matchWidth = _ref.matchWidth,
    matchWidth = _ref$matchWidth === void 0 ? false : _ref$matchWidth,
    _ref$showArrow = _ref.showArrow,
    showArrow = _ref$showArrow === void 0 ? false : _ref$showArrow,
    _ref$shouldCloseOnBlu = _ref.shouldCloseOnBlur,
    shouldCloseOnBlur = _ref$shouldCloseOnBlu === void 0 ? false : _ref$shouldCloseOnBlu,
    onClose = _ref.onClose,
    children = _ref.children,
    classNameSuffix = _ref.classNameSuffix,
    rest = _objectWithoutProperties(_ref, _excluded);
  var modifiers = [{
    name: 'offset',
    options: {
      offset: [0, 5]
    }
  }];
  if (matchWidth) {
    modifiers.push({
      name: 'matchWidth',
      enabled: true,
      fn: function fn(_ref2) {
        var state = _ref2.state;
        state.styles.popper.width = "".concat(state.rects.reference.width, "px");
      },
      phase: 'beforeWrite',
      requires: ['computeStyles']
    });
  }
  if (showArrow) modifiers.push({
    name: 'arrow',
    enabled: true,
    options: {
      padding: 5
    }
  });
  var _usePopper = usePopper({
      placement: placement,
      modifiers: modifiers
    }),
    popper = _usePopper.popper,
    reference = _usePopper.reference;
  reference.current = targetRef.current;
  useCloseOnBlur({
    shouldCloseOnBlur: shouldCloseOnBlur,
    onClose: onClose,
    refs: [targetRef, popper]
  });
  var scopeRef = useRef();
  useEffect(function () {
    var triggerEl = targetRef.current;
    var focusScope = scopeRef.current;
    var onKeyDown = function onKeyDown(e) {
      if (e.key !== 'Tab' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      e.preventDefault();
      focusScope.focusFirstInScope();
    };
    triggerEl.addEventListener('keydown', onKeyDown, true);
    return function () {
      triggerEl.removeEventListener('keydown', onKeyDown, true);
    };
  }, [targetRef]);
  return React.createElement("div", _extends({
    "data-popper-placement": true,
    ref: mergeRefs([popper, ref])
  }, rest, {
    className: clsx(popperStyle(), 'EzPopover', classNameSuffix && "EzPopover-".concat(classNameSuffix))
  }), React.createElement(PortalContext.Provider, {
    value: popper
  }, React.createElement(FocusScope, {
    restoreFocus: true,
    ref: scopeRef
  }, children)));
});
EzPopover.displayName = 'EzPopover';
EzPopoverImpl.displayName = 'EzPopoverImpl';
export default EzPopover;